import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../firebase';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  setDoc, 
  writeBatch,
  serverTimestamp
} from 'firebase/firestore';
import emailjs from '@emailjs/browser';
import { FaLock, FaEye, FaEyeSlash } from 'react-icons/fa';

function Register() {
    const [username, setUsername] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [passwordChecks, setPasswordChecks] = useState({
      length: false,
      uppercase: false,
      number: false,
      symbol: false
    });
    const [usernameChecks, setUsernameChecks] = useState({
      checking: false,
      available: false
    });
    const [emailChecks, setEmailChecks] = useState({
      checking: false,
      available: false,
      valid: false
    });
    const [showPassword, setShowPassword] = useState(false);
  
    const validatePassword = (password) => {
      const regex = /^(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*.]?)(?=.{8,})/;
      return regex.test(password);
    };
  
    const checkPasswordRequirements = (password) => {
      setPasswordChecks({
        length: password.length >= 8,
        uppercase: /[A-Z]/.test(password),
        number: /[0-9]/.test(password),
        symbol: /[!@#$%^&*.]/.test(password)
      });
    };
  
    const sendNotificationEmail = async (userData) => {
        try {
            await emailjs.send(
                process.env.REACT_APP_EMAILJS_SERVICE_ID,
                process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
                {
                    to_email: 'cashsule.app@gmail.com',
                    user_email: userData.email,
                    user_name: userData.username,
                    date: new Date().toLocaleDateString()
                }
            );
        } catch (error) {
            console.error('Error sending notification email:', error);
            // Don't throw the error as this is a non-critical operation
        }
    };
  
    const handleSubmit = async (e) => {
        e.preventDefault();
    
        // Convert username to lowercase for case-insensitive comparison
        const lowercaseUsername = username.toLowerCase();

        if (!validatePassword(password)) {
          setPasswordError('Password must contain at least 1 uppercase letter, 1 number, 1 symbol, and be at least 8 characters long');
          return;
        }
      
        try {
          setError('');
          setLoading(true);
          
          // Check if email exists in users collection
          const usersRef = collection(db, 'users');
          const emailUserQuery = query(usersRef, where('email', '==', email));
          const emailUserSnapshot = await getDocs(emailUserQuery);
          
          // Check if username exists in users collection (case-insensitive)
          const usernameUserQuery = query(usersRef, where('usernameLower', '==', lowercaseUsername));
          const usernameUserSnapshot = await getDocs(usernameUserQuery);
          
          // Check if email exists in pendingRegistrations collection
          const pendingRef = collection(db, 'pendingRegistrations');
          const emailPendingQuery = query(pendingRef, where('email', '==', email));
          const emailPendingSnapshot = await getDocs(emailPendingQuery);
          
          // Check if username exists in pendingRegistrations collection (case-insensitive)
          const usernamePendingQuery = query(pendingRef, where('usernameLower', '==', lowercaseUsername));
          const usernamePendingSnapshot = await getDocs(usernamePendingQuery);

          // Double-check both collections for any case variations
          const allUserDocs = await getDocs(collection(db, 'users'));
          const allPendingDocs = await getDocs(collection(db, 'pendingRegistrations'));
          
          const existingUsernames = allUserDocs.docs
            .map(doc => doc.data().username?.toLowerCase())
            .filter(Boolean);
          const existingPendingUsernames = allPendingDocs.docs
            .map(doc => doc.data().username?.toLowerCase())
            .filter(Boolean);
          
          if (existingUsernames.includes(lowercaseUsername) || 
              existingPendingUsernames.includes(lowercaseUsername)) {
            setError('Username already taken');
            setLoading(false);
            return;
          }

          if (!emailUserSnapshot.empty || !emailPendingSnapshot.empty) {
            setError('Email already registered');
            setLoading(false);
            return;
          }

          if (!usernameUserSnapshot.empty || !usernamePendingSnapshot.empty) {
            setError('Username already taken');
            setLoading(false);
            return;
          }
      
          const userData = {
            username,
            usernameLower: lowercaseUsername, // Store lowercase version for future queries
            email,
            password: btoa(password),
            status: 'pending',
            createdAt: new Date().toISOString(),
            approved: false,
            isAdmin: false
          };

          // Create temporary user document
          const tempUserRef = doc(collection(db, 'pendingRegistrations'));
          await setDoc(tempUserRef, userData);

          // Send notification email
          await sendNotificationEmail(userData);

          const createNotification = async () => {
            try {
              // Get all admin users
              const adminUsersSnapshot = await getDocs(
                query(collection(db, 'users'), where('isAdmin', '==', true))
              );
              
              // Create a notification for each admin
              const notifications = adminUsersSnapshot.docs.map(adminDoc => ({
                userId: adminDoc.id,
                message: `New registration request from ${username} (${email})`,
                createdAt: serverTimestamp(),
                read: false,
                type: 'registration'
              }));

              // Batch write all notifications
              const batch = writeBatch(db);
              notifications.forEach(notification => {
                const notificationRef = doc(collection(db, 'notifications'));
                batch.set(notificationRef, notification);
              });

              await batch.commit();
            } catch (error) {
              console.error('Error creating notifications:', error);
              // Don't throw the error as this is a non-critical operation
            }
          };

          await createNotification();
      
          setShowModal(true);
          setLoading(false);
    
        } catch (error) {
          console.error('Registration error details:', {
            code: error.code,
            message: error.message,
            fullError: error
          });
          setLoading(false);
          setError('Failed to submit registration');
        }
    };

    const checkUsernameAvailability = async (username) => {
      if (!username) {
        setUsernameChecks({ checking: false, available: false });
        return;
      }
      
      try {
        setUsernameChecks({ checking: true, available: false });
        const lowercaseUsername = username.toLowerCase();
        
        // Check in users collection
        const usersRef = collection(db, 'users');
        const usernameUserQuery = query(usersRef, where('usernameLower', '==', lowercaseUsername));
        const usernameUserSnapshot = await getDocs(usernameUserQuery);
        
        // Check in pendingRegistrations collection
        const pendingRef = collection(db, 'pendingRegistrations');
        const usernamePendingQuery = query(pendingRef, where('usernameLower', '==', lowercaseUsername));
        const usernamePendingSnapshot = await getDocs(usernamePendingQuery);
        
        const isAvailable = usernameUserSnapshot.empty && usernamePendingSnapshot.empty;
        setUsernameChecks({ checking: false, available: isAvailable });
      } catch (error) {
        setUsernameChecks({ checking: false, available: false });
      }
    };

    const checkEmailAvailability = async (email) => {
      // First check if it's a valid email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const isValidFormat = emailRegex.test(email);
      
      if (!email || !isValidFormat) {
        setEmailChecks(prev => ({ ...prev, checking: false, available: false, valid: false }));
        return;
      }
      
      try {
        setEmailChecks(prev => ({ ...prev, checking: true, available: false, valid: isValidFormat }));
        
        // Check in users collection
        const usersRef = collection(db, 'users');
        const emailUserQuery = query(usersRef, where('email', '==', email));
        const emailUserSnapshot = await getDocs(emailUserQuery);
        
        // Check in pendingRegistrations collection
        const pendingRef = collection(db, 'pendingRegistrations');
        const emailPendingQuery = query(pendingRef, where('email', '==', email));
        const emailPendingSnapshot = await getDocs(emailPendingQuery);
        
        const isAvailable = emailUserSnapshot.empty && emailPendingSnapshot.empty;
        setEmailChecks({ checking: false, available: isAvailable, valid: isValidFormat });
      } catch (error) {
        setEmailChecks(prev => ({ ...prev, checking: false, available: false }));
      }
    };

    // Modal Component
    const Modal = () => {
        if (!showModal) return null;

        return (
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50">
                <div className="relative bg-white rounded-lg p-8 m-4 max-w-xl w-full shadow-lg">
                    <div className="text-center">
                        <h3 className="text-xl font-semibold text-gray-900 mb-4">
                            Registration Submitted Successfully!
                        </h3>
                        <p className="text-gray-600 mb-6">
                            Your registration is under review. 
                            You'll get an email once a decision is made, 
                            so check your inbox and spam folder for updates.
                        </p>
                        <Link
                            to="/"
                            className="bg-primary-600 text-white px-4 py-2 rounded hover:bg-primary-700"
                        >
                            Return to Login
                        </Link>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
            <div className="sm:mx-auto sm:w-full sm:max-w-md">
                <h2 className="text-center text-3xl font-extrabold text-gray-900">
                    Create your account
                </h2>
            </div>

            <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
                <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
                    {error && (
                        <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">
                            {error}
                        </div>
                    )}

                    <form className="space-y-6" onSubmit={handleSubmit}>
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                                Email address
                            </label>
                            <div className="mt-1">
                                <input
                                    id="email"
                                    type="email"
                                    required
                                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                                    value={email}
                                    placeholder="Enter your email"
                                    onChange={(e) => {
                                        setEmail(e.target.value);
                                        checkEmailAvailability(e.target.value);
                                    }}
                                />
                                {email && (
                                    <div className="mt-2">
                                        {emailChecks.checking ? (
                                            <p className="text-sm text-gray-500">Checking availability...</p>
                                        ) : (
                                            <>
                                                {!emailChecks.valid && (
                                                    <p className="text-sm text-red-600 flex items-center">
                                                        ✗ Please enter a valid email address
                                                    </p>
                                                )}
                                                {emailChecks.valid && (
                                                    <p className={`text-sm flex items-center ${emailChecks.available ? 'text-green-600' : 'text-red-600'}`}>
                                                        {emailChecks.available ? '✓ Email is available' : '✗ Email is already registered'}
                                                    </p>
                                                )}
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div>
                            <label htmlFor="username" className="block text-sm font-medium text-gray-700">
                                Username
                            </label>
                            <div className="mt-1">
                                <input
                                    id="username"
                                    type="text"
                                    required
                                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                                    value={username}
                                    placeholder="Enter your username"
                                    onChange={(e) => {
                                        setUsername(e.target.value);
                                        checkUsernameAvailability(e.target.value);
                                    }}
                                />
                                {username && (
                                    <div className="mt-2">
                                        {usernameChecks.checking ? (
                                            <p className="text-sm text-gray-500">Checking availability...</p>
                                        ) : (
                                            <p className={`text-sm flex items-center ${usernameChecks.available ? 'text-green-600' : 'text-red-600'}`}>
                                                {usernameChecks.available ? '✓ Username is available' : '✗ Username is not available'}
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                        
                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                                New Password
                            </label>
                            <div className="mt-1">
                                <div className="relative">
                                    <FaLock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                    <input
                                        id="password"
                                        type={showPassword ? "text" : "password"}
                                        required
                                        className="appearance-none block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                                        value={password}
                                        placeholder="Enter your password"
                                        onChange={(e) => {
                                            setPassword(e.target.value);
                                            checkPasswordRequirements(e.target.value);
                                        }}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    >
                                        {showPassword ? <FaEyeSlash className="h-5 w-5" /> : <FaEye className="h-5 w-5" />}
                                    </button>
                                </div>
                                {passwordError && (
                                    <p className="mt-2 text-sm text-red-600">{passwordError}</p>
                                )}
                                <div className="mt-2 space-y-2">
                                    <p className={`text-sm flex items-center ${passwordChecks.length ? 'text-green-600' : 'text-gray-500'}`}>
                                        {passwordChecks.length ? '✓' : '○'} At least 8 characters long
                                    </p>
                                    <p className={`text-sm flex items-center ${passwordChecks.uppercase ? 'text-green-600' : 'text-gray-500'}`}>
                                        {passwordChecks.uppercase ? '✓' : '○'} At least one uppercase letter
                                    </p>
                                    <p className={`text-sm flex items-center ${passwordChecks.number ? 'text-green-600' : 'text-gray-500'}`}>
                                        {passwordChecks.number ? '✓' : '○'} At least one number
                                    </p>
                                    <p className={`text-sm flex items-center ${passwordChecks.symbol ? 'text-green-600' : 'text-gray-500'}`}>
                                        {passwordChecks.symbol ? '✓' : '○'} At least one symbol (!@#$%^&*.)
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div>
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                            >
                                {loading ? 'Creating Account...' : 'Register'}
                            </button>
                        </div>
                    </form>

                    <div className="mt-6">
                        <Link
                            to="/login"
                            className="text-sm font-medium text-primary-600 hover:text-primary-500 flex justify-center"
                        >
                            Already have an account? Sign in
                        </Link>
                    </div>
                </div>
            </div>
            <Modal />
        </div>
    );
}

export default Register;